import { useState, useEffect } from 'react';
import { BottomSheet } from '@/shared/ui';
import { useFilterStore } from '../store';
import { getPeriodLabel } from '../utils';
import { haptic } from '@/shared/utils/telegram';
import type { TimePreset, EventType } from '@/shared/types';

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
}

const PERIOD_OPTIONS = [
  { value: 'today' as const, label: '1D', fullLabel: 'Today' },
  { value: 'yesterday' as const, label: 'YD', fullLabel: 'Yesterday' },
  { value: 'this_week' as const, label: '7D', fullLabel: 'This Week' },
  { value: 'this_month' as const, label: 'M', fullLabel: 'This Month' },
  { value: 'last_month' as const, label: 'LM', fullLabel: 'Last Month' },
  { value: 'this_quarter' as const, label: 'Q', fullLabel: 'This Quarter' },
  { value: 'this_year' as const, label: 'YTD', fullLabel: 'This Year' },
];

const EVENT_TYPE_OPTIONS: EventType[] = ['QUALIFIED_CPA', 'FTD', 'REG', 'REDEP', 'CLICK'];

export function FilterSheet({ open, onClose }: FilterSheetProps) {
  const period = useFilterStore((s) => s.period);
  const brands = useFilterStore((s) => s.brands);
  const eventTypes = useFilterStore((s) => s.eventTypes);
  const sources = useFilterStore((s) => s.sources);
  const affiliateId = useFilterStore((s) => s.affiliateId);
  const trackingTag = useFilterStore((s) => s.trackingTag);
  const tagSender = useFilterStore((s) => s.tagSender);
  const tagGeo = useFilterStore((s) => s.tagGeo);
  const tagSheetDate = useFilterStore((s) => s.tagSheetDate);
  const setPeriod = useFilterStore((s) => s.setPeriod);
  const setBrands = useFilterStore((s) => s.setBrands);
  const setEventTypes = useFilterStore((s) => s.setEventTypes);
  const setSources = useFilterStore((s) => s.setSources);
  const setAffiliateId = useFilterStore((s) => s.setAffiliateId);
  const setTrackingTag = useFilterStore((s) => s.setTrackingTag);
  const setTagSender = useFilterStore((s) => s.setTagSender);
  const setTagGeo = useFilterStore((s) => s.setTagGeo);
  const setTagSheetDate = useFilterStore((s) => s.setTagSheetDate);
  const reset = useFilterStore((s) => s.reset);

  const [localPeriod, setLocalPeriod] = useState<TimePreset | 'custom'>(period);
  const [localEventTypes, setLocalEventTypes] = useState<EventType[]>(eventTypes);
  const [localSources, setLocalSources] = useState<string[]>(sources);
  const [localAffiliateId, setLocalAffiliateId] = useState(affiliateId || '');
  const [localTrackingTag, setLocalTrackingTag] = useState(trackingTag || '');
  const [localTagSender, setLocalTagSender] = useState(tagSender || '');
  const [localTagGeo, setLocalTagGeo] = useState(tagGeo || '');
  const [localTagSheetDate, setLocalTagSheetDate] = useState(tagSheetDate || '');
  const [availableSources, setAvailableSources] = useState<string[]>([]);

  // Fetch available traffic sources from API
  useEffect(() => {
    if (open) {
      fetch('/events/analytics/sources')
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data)) setAvailableSources(data);
        })
        .catch(() => {});
    }
  }, [open]);

  const handleApply = () => {
    haptic.impact('light');
    setPeriod(localPeriod);
    setEventTypes(localEventTypes);
    setSources(localSources);
    setAffiliateId(localAffiliateId.trim() || undefined);
    setTrackingTag(localTrackingTag.trim() || undefined);
    setTagSender(localTagSender.trim() || undefined);
    setTagGeo(localTagGeo.trim() || undefined);
    setTagSheetDate(localTagSheetDate.trim() || undefined);
    onClose();
  };

  const handleReset = () => {
    haptic.selection();
    reset();
    setLocalPeriod('today');
    setLocalEventTypes([]);
    setLocalSources([]);
    setLocalAffiliateId('');
    setLocalTrackingTag('');
    setLocalTagSender('');
    setLocalTagGeo('');
    setLocalTagSheetDate('');
  };

  const handleEventTypeToggle = (eventType: EventType) => {
    haptic.selection();
    if (localEventTypes.includes(eventType)) {
      setLocalEventTypes(localEventTypes.filter((t) => t !== eventType));
    } else {
      setLocalEventTypes([...localEventTypes, eventType]);
    }
  };

  const handleSourceToggle = (source: string) => {
    haptic.selection();
    if (localSources.includes(source)) {
      setLocalSources(localSources.filter((s) => s !== source));
    } else {
      setLocalSources([...localSources, source]);
    }
  };

  const inputClassName = "w-full h-11 px-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20 placeholder:opacity-50";

  return (
    <BottomSheet isOpen={open} onClose={onClose} title="Filters">
      <div className="space-y-6">
        {/* Period Section */}
        <div className="space-y-3">
          <div className="text-sm font-bold text-[var(--tg-theme-text-color)]">Time Period</div>
          <div className="flex p-1 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl overflow-x-auto scrollbar-hide">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => {
                  haptic.selection();
                  setLocalPeriod(p.value);
                }}
                title={p.fullLabel}
                className={`flex-1 py-2.5 text-sm font-black rounded-lg transition-all min-w-[44px] ${
                  localPeriod === p.value
                    ? 'bg-[var(--tg-theme-bg-color)] shadow-sm text-[var(--tg-theme-link-color)]'
                    : 'text-[var(--tg-theme-hint-color)]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-[var(--tg-theme-hint-color)]">
            Currently showing: {getPeriodLabel(period)}
          </div>
        </div>

        {/* Event Types Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-bold text-[var(--tg-theme-text-color)]">Event Types</div>
            {localEventTypes.length > 0 && (
              <button
                className="text-xs font-bold text-[var(--tg-theme-link-color)] active:opacity-70 transition-opacity"
                onClick={() => {
                  haptic.impact('light');
                  setLocalEventTypes([]);
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {EVENT_TYPE_OPTIONS.map((eventType) => {
              const displayLabel = eventType === 'QUALIFIED_CPA' ? 'CPA' : eventType;
              return (
                <button
                  key={eventType}
                  onClick={() => handleEventTypeToggle(eventType)}
                  className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider transition-all active:scale-95 ${
                    localEventTypes.includes(eventType)
                      ? 'bg-[var(--tg-theme-link-color)] text-white'
                      : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)]'
                  }`}
                >
                  {displayLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Traffic Source Section */}
        {availableSources.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-[var(--tg-theme-text-color)]">Traffic Source</div>
              {localSources.length > 0 && (
                <button
                  className="text-xs font-bold text-[var(--tg-theme-link-color)] active:opacity-70 transition-opacity"
                  onClick={() => {
                    haptic.impact('light');
                    setLocalSources([]);
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {availableSources.map((src) => (
                <button
                  key={src}
                  onClick={() => handleSourceToggle(src)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 ${
                    localSources.includes(src)
                      ? 'bg-[var(--tg-theme-link-color)] text-white'
                      : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)]'
                  }`}
                >
                  {src}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tracking Tag Filters */}
        <div className="space-y-3">
          <div className="text-sm font-bold text-[var(--tg-theme-text-color)]">Tracking Tag</div>
          <input
            type="text"
            value={localTrackingTag}
            onChange={(e) => setLocalTrackingTag(e.target.value)}
            placeholder="Full tag: WA_FR_DIM_20"
            className={inputClassName}
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              value={localTagSender}
              onChange={(e) => setLocalTagSender(e.target.value)}
              placeholder="Sender"
              className={inputClassName}
            />
            <input
              type="text"
              value={localTagGeo}
              onChange={(e) => setLocalTagGeo(e.target.value)}
              placeholder="Geo"
              className={inputClassName}
            />
            <input
              type="text"
              value={localTagSheetDate}
              onChange={(e) => setLocalTagSheetDate(e.target.value)}
              placeholder="Sheet"
              className={inputClassName}
            />
          </div>
          <div className="text-[10px] text-[var(--tg-theme-hint-color)]">
            Format: Sender_Geo_Sheet (e.g. WA_FR_DIM_20)
          </div>
        </div>

        {/* Affiliate ID */}
        <div className="space-y-3">
          <div className="text-sm font-bold text-[var(--tg-theme-text-color)]">Affiliate ID</div>
          <input
            type="text"
            value={localAffiliateId}
            onChange={(e) => setLocalAffiliateId(e.target.value)}
            placeholder="e.g. AFF_12345"
            className={inputClassName}
          />
        </div>

        {/* Brands Section */}
        {brands.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-[var(--tg-theme-text-color)]">Active Brands</div>
              <button
                className="text-xs font-bold text-[var(--tg-theme-link-color)] active:opacity-70 transition-opacity"
                onClick={() => {
                  haptic.impact('light');
                  setBrands([]);
                }}
              >
                Clear
              </button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-[40px] overflow-hidden">
              {brands.slice(0, 3).map((brand) => (
                <span
                  key={brand}
                  className="text-xs font-bold px-3 py-1.5 bg-[var(--tg-theme-link-color)]/10 text-[var(--tg-theme-link-color)] rounded-full border border-[var(--tg-theme-link-color)]/20"
                >
                  {brand}
                </span>
              ))}
              {brands.length > 3 && (
                <span className="text-xs font-bold px-3 py-1.5 bg-[var(--tg-theme-hint-color)]/10 text-[var(--tg-theme-hint-color)] rounded-full border border-[var(--tg-theme-hint-color)]/20">
                  +{brands.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleReset}
            className="flex-1 py-3 px-4 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] font-bold text-sm active:scale-95 transition-transform border border-black/5 dark:border-white/5"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            className="flex-[2] py-3 px-4 rounded-xl bg-[var(--tg-theme-link-color)] text-white font-bold text-sm active:scale-95 transition-transform shadow-lg"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
